/**
 * 언론보도 정적 데이터.
 *
 * 백엔드 없이 코드 내 배열로 관리한다. 새 기사가 나오면 이 배열에 항목을 추가하고 배포한다.
 *
 * 이미지는 언론사 CDN 원본 URL을 그대로 참조(핫링크)한다 — 저작권(재판매·DB저장 금지) 표기가 있는
 * 이미지를 로컬(`public/`)에 다운로드해 저장하지 않는다.
 */

export type NewsContentBlock =
  | { type: "paragraph"; text: string }
  | {
      type: "image";
      src: string;
      /** 원본 CDN 이 내주는 같은 사진의 크기별 판(폭 서술자 `srcset`) — 화면 폭에 맞는 판 하나만 받는다 */
      srcSet?: string;
      alt?: string;
      /** 원본 가로·세로(px) — 있으면 사진 자리를 미리 잡아, 사진이 들어와도 아래 글이 밀리지 않는다 */
      width?: number;
      height?: number;
    }
  | { type: "heading"; text: string }
  | { type: "list"; items: string[] };

export interface NewsArticle {
  /** URL slug (`/news/[id]`) */
  id: string;
  title: string;
  /**
   * 리스트 행 요약.
   * 상세 페이지에서는 `content` 에 paragraph 블록이 없을 때만 본문 자리에 표시된다
   * (읽을 본문이 없는 사진 위주 기사 대비 — press-news 스펙의 "원문 콘텐츠 충실 표시").
   */
  summary: string;
  source: string;
  author?: string;
  /** ISO 8601 게재일 */
  publishedAt: string;
  /** 원본 CDN 이미지 URL — 로컬 저장 금지, 핫링크만 사용 */
  thumbnail: string;
  /** 썸네일과 같은 사진의 원본 CDN 크기별 판(폭 서술자 `srcset`) */
  thumbnailSrcSet?: string;
  /** "원문보기" 버튼이 여는 원본 기사 URL */
  originalUrl: string;
  /**
   * 외국어 원문을 한국어로 옮긴 기사의 원문 언어. 있으면 상세 본문 끝에 번역 안내가 붙는다.
   * 제목·요약·본문은 원문을 빼거나 보태지 않은 번역문이어야 한다(press-news).
   */
  translatedFrom?: "ja";
  content: NewsContentBlock[];
}

/**
 * ISO 8601 게재일을 현재 언어(`language`)의 로컬 표기로 변환한다.
 * 파싱 불가한 값은 원본 문자열을 그대로 돌려준다.
 */
export function formatPublishedDate(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export const NEWS_ARTICLES: readonly NewsArticle[] = [
  {
    id: "1188653",
    title: "Liberaware Korea, 한국의 드론 기반 인프라 관리 플랫폼 기업 ALTIVION과 업무 제휴",
    summary:
      "2026년 6월 22일, Liberaware(이하 리베라웨어)의 한국 법인인 Liberaware Korea는 한국 서울을 거점으로 드론을 활용한 3D 인프라 관리 시스템을 제공하는 기업 ALTIVION과 한국 토목 인프라 분야의 사업 개발·확대를 목적으로 한 업무 제휴에 관한 양해각서(MOU)를 체결했다고 발표했다.",
    source: "드론저널",
    author: "드론저널 편집부",
    publishedAt: "2026-07-01T12:02:46+09:00",
    thumbnail: "https://drone-journal.impress.co.jp/img/drone/docs/1188/653/1188653_01_o.jpg",
    thumbnailSrcSet:
      "https://drone-journal.impress.co.jp/img/drone/docs/1188/653/1188653_01_l.jpg 600w, https://drone-journal.impress.co.jp/img/drone/docs/1188/653/1188653_01_o.jpg 800w",
    originalUrl: "https://drone-journal.impress.co.jp/docs/news/1188653.html",
    translatedFrom: "ja",
    content: [
      {
        type: "image",
        src: "https://drone-journal.impress.co.jp/img/drone/docs/1188/653/1188653_01_o.jpg",
        srcSet:
          "https://drone-journal.impress.co.jp/img/drone/docs/1188/653/1188653_01_l.jpg 600w, https://drone-journal.impress.co.jp/img/drone/docs/1188/653/1188653_01_o.jpg 800w",
        width: 800,
        height: 534,
      },
      {
        type: "paragraph",
        text: "2026년 6월 22일, Liberaware(이하 리베라웨어)의 한국 법인인 Liberaware Korea는 한국 서울을 거점으로 드론을 활용한 3D 인프라 관리 시스템을 제공하는 기업 ALTIVION과 한국 토목 인프라 분야의 사업 개발·확대를 목적으로 한 업무 제휴에 관한 양해각서(MOU)를 체결했다고 발표했다.",
      },
      {
        type: "paragraph",
        text: "리베라웨어가 보유한 실내 협소 공간 특화형 소형 드론 기술과 ALTIVION이 제공하는 드론 데이터 관리·시각화 플랫폼을 결합해, 한국의 토목 인프라 점검·유지관리 분야에서 새로운 사업 기회를 창출하는 것을 목표로 한다.",
      },
      {
        type: "paragraph",
        text: "리베라웨어는 실내 협소 공간의 점검·계측에 특화된 소형 드론 'IBIS2'의 개발·제조·판매 및 현장 운용을 해 왔다. ALTIVION은 대규모 드론 영상과 공간 데이터를 프로젝트 단위로 저장·활용하고, 시계열 점검, 2D·3D 공간 데이터 시각화, 시설물 점검·유지관리, 측량, 건설·토목 현장 모니터링 등을 지원하는 서비스 'DroneSquare'를 제공하고 있다.",
      },
      {
        type: "paragraph",
        text: "양사는 앞으로 서로의 기술과 지식을 결합해 한국의 토목 인프라·건설 현장에서의 드론 활용을 '일회성 촬영·점검'에서 '지속적인 데이터 활용'으로 전환하고, 한국 시장에서의 사업 규모 확대를 노린다.",
      },
      { type: "heading", text: "협업 내용" },
      {
        type: "paragraph",
        text: "이번 업무 제휴에 따라 양사는 아래 협력 분야를 포함한 폭넓은 사업 연계를 위한 검토에 착수한다.",
      },
      {
        type: "list",
        items: [
          "한국 시장에서의 공동 마케팅 활동",
          "제품 및 서비스에 관한 정보 공유",
          "협력적인 프로모션 활동",
          "토목 인프라 점검·유지관리 분야의 사업 기회 탐색",
          "건설·토목 현장, 상하수도, 교량, 도로 등을 대상으로 한 고객용 시연, PoC, 실증 기회 검토",
        ],
      },
    ],
  },
  {
    id: "6210190",
    title: "케이워터기술, 알티비온과 '드론·AI 영상분석 솔루션' MOU 체결",
    summary:
      "케이워터기술은 알티비온과 '드론·AI 영상분석 설루션 개발 및 사업 협력'을 위한 '양해각서'(MOU)를 체결했다고 27일 밝혔다.",
    source: "뉴스1",
    author: "김낙희 기자",
    publishedAt: "2026-06-27T14:51:28+09:00",
    thumbnail: "https://i3n.news1.kr/system/photos/2026/6/27/7979590/high.jpg",
    thumbnailSrcSet:
      "https://i3n.news1.kr/system/photos/2026/6/27/7979590/article.jpg 560w, https://i3n.news1.kr/system/photos/2026/6/27/7979590/high.jpg 1400w",
    originalUrl: "https://www.news1.kr/local/daejeon-chungnam/6210190",
    content: [
      {
        type: "image",
        src: "https://i3n.news1.kr/system/photos/2026/6/27/7979590/high.jpg",
        srcSet:
          "https://i3n.news1.kr/system/photos/2026/6/27/7979590/article.jpg 560w, https://i3n.news1.kr/system/photos/2026/6/27/7979590/high.jpg 1400w",
        width: 1400,
        height: 958,
        alt: "정승용 케이워터기술 부사장(왼쪽)과 강서연 알티비온 대표가 MOU를 체결하고 기념촬영을 하고 있다.(케이워터기술 제공. 재판매 및 DB금지)",
      },
      {
        type: "paragraph",
        text: "(대전=뉴스1) 김낙희 기자 = 케이워터기술은 알티비온과 '드론·AI 영상분석 설루션 개발 및 사업 협력'을 위한 '양해각서'(MOU)를 체결했다고 27일 밝혔다.",
      },
      {
        type: "paragraph",
        text: "케이워터기술은 국가 기간시설인 광역상수도 및 다목적 댐 설비의 점검 정비 전문기업이다. 대전시에 본사를 두고 4개 유역본부와 전국 48개 사업소를 운영하고 있다.",
      },
      {
        type: "paragraph",
        text: "이번 MOU 체결은 양사가 점검 정비 현장에 특화된 드론 및 AI 영상분석 솔루션을 공동으로 개발하고 향후 관련 공동 사업화를 추진하기 위해 마련했다.",
      },
      {
        type: "paragraph",
        text: "케이워터기술은 공동 개발된 솔루션을 자사가 역점 구축 중인 '스마트 점검 정비 플랫폼'의 핵심 기술 라인업으로 활용할 계획이다.",
      },
      {
        type: "paragraph",
        text: "양사 주요 협력 분야는 △드론·AI 기반 영상분석 솔루션 공동 기획 및 설계 △현장 데이터 디지털화 및 AI 학습데이터 표준화 방안 연구 등이다.",
      },
      {
        type: "paragraph",
        text: "케이워터기술은 수자원 관련 현장 노하우와 AI 솔루션 검증을 위한 테스트베드를 제공하고, 알티비온은 드론 기반 공간 데이터 처리와 AI 영상분석 알고리즘 개발 역량을 바탕으로 시스템 아키텍처 설계를 담당한다.",
      },
      {
        type: "paragraph",
        text: '케이워터기술 관계자는 "이번 협력을 계기로 드론, AI, 디지털 데이터 기술을 융합한 차세대 시설관리 체계를 구축할 계획"이라고 했다.',
      },
      {
        type: "paragraph",
        text: "lucky@news1.kr",
      },
    ],
  },
];
